"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DistributorClient = void 0;
const web3_js_1 = require("@solana/web3.js");
const anchor_1 = require("@project-serum/anchor");
const common_sdk_1 = require("@orca-so/common-sdk");
const token_utils_1 = require("@saberhq/token-utils");
const utils_1 = require("../utils");
const buffer_layout_1 = require("@solana/buffer-layout");
const buffer_layout_utils_1 = require("@solana/buffer-layout-utils");
const spl_token_1 = require("@solana/spl-token");
class DistributorClient {
    constructor(ctx, pda) {
        this.ctx = ctx;
        this.pda = pda;
    }
    newDistributor(mint, root, clawbackReceiver, maxTotalClaim, maxNumNodes, startTs, endTs, version = new anchor_1.BN(1)) {
        return __awaiter(this, void 0, void 0, function* () {
            const distributor = this.pda.distributor(mint, version);
            const clawbackReceiverATA = (0, token_utils_1.getATAAddressSync)({ mint, owner: clawbackReceiver });
            const tx = new common_sdk_1.TransactionBuilder(this.ctx.provider.connection, this.ctx.provider.wallet);
            const clawbackTokenAccountInfo = yield this.ctx.connection.getAccountInfo(clawbackReceiverATA);
            if (clawbackTokenAccountInfo === null) {
                tx.addInstruction({
                    instructions: [
                        spl_token_1.Token.createAssociatedTokenAccountInstruction(token_utils_1.ASSOCIATED_TOKEN_PROGRAM_ID, token_utils_1.TOKEN_PROGRAM_ID, mint, clawbackReceiverATA, clawbackReceiver, this.ctx.wallet.publicKey)
                    ],
                    cleanupInstructions: [],
                    signers: [],
                });
            }
            const tokenVault = (0, token_utils_1.getATAAddressSync)({ mint, owner: distributor.key });
            const newDistributorIx = yield this.ctx.methods.newDistributor({
                accounts: {
                    distributor: distributor.key,
                    clawbackReceiver: clawbackReceiverATA,
                    mint,
                    tokenVault,
                    admin: this.ctx.wallet.publicKey,
                },
                inputs: {
                    version,
                    root: (0, utils_1.toBytes32Array)(root),
                    maxTotalClaim,
                    maxNumNodes,
                    startTs,
                    endTs,
                },
            });
            if (newDistributorIx.ix) {
                tx.addInstruction(newDistributorIx.ix);
            }
            return tx;
        });
    }
    setAdmin(mint, newAdmin, version = new anchor_1.BN(1)) {
        return __awaiter(this, void 0, void 0, function* () {
            const distributor = this.pda.distributor(mint, version);
            const distributorData = yield this.ctx.program.account.merkleDistributor.fetch(distributor.key);
            if (!distributorData) {
                throw new Error(`Distributor of token ${mint.toBase58()} version ${version.toString()} not found`);
            }
            if (distributorData.admin.toBase58() !== this.ctx.wallet.publicKey.toBase58()) {
                throw new Error(`Admin of distributor ${distributor.key.toBase58()} is not the current wallet`);
            }
            const tx = (yield this.ctx.methods.setAdmin({
                accounts: {
                    distributor: distributor.key,
                    admin: this.ctx.wallet.publicKey,
                    newAdmin
                },
                inputs: {},
            })).toTx();
            return tx;
        });
    }
    setClawbackReceiver(mint, clawbackReceiver, version = new anchor_1.BN(1)) {
        return __awaiter(this, void 0, void 0, function* () {
            const distributor = this.pda.distributor(mint, version);
            const distributorData = yield this.ctx.program.account.merkleDistributor.fetch(distributor.key);
            if (!distributorData) {
                throw new Error(`Distributor of token ${mint.toBase58()} version ${version.toString()} not found`);
            }
            if (distributorData.admin.toBase58() !== this.ctx.wallet.publicKey.toBase58()) {
                throw new Error(`Admin of distributor ${distributor.key.toBase58()} is not the current wallet`);
            }
            const clawbackReceiverATA = (0, token_utils_1.getATAAddressSync)({ mint, owner: clawbackReceiver });
            const tx = new common_sdk_1.TransactionBuilder(this.ctx.provider.connection, this.ctx.provider.wallet);
            const clawbackTokenAccountInfo = yield this.ctx.connection.getAccountInfo(clawbackReceiverATA);
            if (clawbackTokenAccountInfo === null) {
                tx.addInstruction({
                    instructions: [
                        spl_token_1.Token.createAssociatedTokenAccountInstruction(token_utils_1.ASSOCIATED_TOKEN_PROGRAM_ID, token_utils_1.TOKEN_PROGRAM_ID, mint, clawbackReceiverATA, clawbackReceiver, this.ctx.wallet.publicKey)
                    ],
                    cleanupInstructions: [],
                    signers: [],
                });
            }
            const setClawbackReceiverIx = yield this.ctx.methods.setClawbackReceiver({
                accounts: {
                    distributor: distributor.key,
                    admin: this.ctx.wallet.publicKey,
                    newClawbackAccount: clawbackReceiverATA,
                },
                inputs: {},
            });
            if (setClawbackReceiverIx.ix) {
                tx.addInstruction(setClawbackReceiverIx.ix);
            }
            return tx;
        });
    }
    setMerkleRoot(mint, root, maxTotalClaim, maxNumNodes, version = new anchor_1.BN(1)) {
        return __awaiter(this, void 0, void 0, function* () {
            const distributor = this.pda.distributor(mint, version);
            const distributorData = yield this.ctx.program.account.merkleDistributor.fetch(distributor.key);
            if (!distributorData) {
                throw new Error(`Distributor of token ${mint.toBase58()} version ${version.toString()} not found`);
            }
            if (distributorData.admin.toBase58() !== this.ctx.wallet.publicKey.toBase58()) {
                throw new Error(`Admin of distributor ${distributor.key.toBase58()} is not the current wallet`);
            }
            const tx = (yield this.ctx.methods.setMerkleRoot({
                accounts: {
                    distributor: distributor.key,
                    admin: this.ctx.wallet.publicKey,
                },
                inputs: {
                    root: (0, utils_1.toBytes32Array)(root),
                    maxTotalClaim,
                    maxNumNodes,
                },
            })).toTx();
            return tx;
        });
    }
    setTimestamp(mint, startTs, endTs, version = new anchor_1.BN(1)) {
        return __awaiter(this, void 0, void 0, function* () {
            const distributor = this.pda.distributor(mint, version);
            const distributorData = yield this.ctx.program.account.merkleDistributor.fetch(distributor.key);
            if (!distributorData) {
                throw new Error(`Distributor of token ${mint.toBase58()} version ${version.toString()} not found`);
            }
            if (distributorData.admin.toBase58() !== this.ctx.wallet.publicKey.toBase58()) {
                throw new Error(`Admin of distributor ${distributor.key.toBase58()} is not the current wallet`);
            }
            const tx = (yield this.ctx.methods.setTimestamp({
                accounts: {
                    distributor: distributor.key,
                    admin: this.ctx.wallet.publicKey,
                },
                inputs: {
                    startTs,
                    endTs,
                },
            })).toTx();
            return tx;
        });
    }
    deposit(mint, amount, version = new anchor_1.BN(1)) {
        return __awaiter(this, void 0, void 0, function* () {
            const distributor = this.pda.distributor(mint, version);
            let distributorData;
            try {
                distributorData = yield this.ctx.fetcher.getOneMerkleDistributor(distributor.key);
            }
            catch (error) {
                throw new Error(`Distributor of token ${mint.toBase58()} version ${version.toString()} not found`);
            }
            if (!distributorData) {
                throw new Error(`Distributor of token ${mint.toBase58()} version ${version.toString()} not found`);
            }
            if (distributorData.admin.toBase58() !== this.ctx.wallet.publicKey.toBase58()) {
                throw new Error(`Admin of distributor ${distributor.key.toBase58()} is not the current wallet`);
            }
            const fromAssociatedTokenAccount = (0, token_utils_1.getATAAddressSync)({
                mint,
                owner: this.ctx.wallet.publicKey
            });
            // tokenVault is initialized when creating the distributor
            const tokenVault = distributorData.tokenVault;
            const tx = new common_sdk_1.TransactionBuilder(this.ctx.provider.connection, this.ctx.provider.wallet);
            const keys = (0, utils_1.addSigners)([
                { pubkey: fromAssociatedTokenAccount, isSigner: false, isWritable: true },
                { pubkey: tokenVault, isSigner: false, isWritable: true },
            ], this.ctx.wallet.publicKey, []);
            const transferInstructionData = (0, buffer_layout_1.struct)([(0, buffer_layout_1.u8)('instruction'), (0, buffer_layout_utils_1.u64)('amount')]);
            const data = Buffer.alloc(transferInstructionData.span);
            transferInstructionData.encode({ instruction: utils_1.TokenInstruction.Transfer, amount: BigInt(amount.toString()) }, data);
            tx.addInstruction({
                instructions: [new web3_js_1.TransactionInstruction({
                        keys,
                        programId: token_utils_1.TOKEN_PROGRAM_ID,
                        data
                    })],
                cleanupInstructions: [],
                signers: []
            });
            return tx;
        });
    }
    newClaim(mint, index, amount, proof, version = new anchor_1.BN(1)) {
        return __awaiter(this, void 0, void 0, function* () {
            if (proof.length === 0) {
                throw new Error("Proof is empty");
            }
            const distributor = this.pda.distributor(mint, version);
            const claimStatus = this.pda.claim_status(this.ctx.wallet.publicKey, distributor.key);
            try {
                yield this.ctx.program.account.claimStatus.fetch(claimStatus.key);
                throw Error(`Already claimed`);
            }
            catch (error) {
                if (!error.message.includes("Account does not exist")) {
                    throw error;
                }
            }
            const from = (0, token_utils_1.getATAAddressSync)({ mint, owner: distributor.key });
            const to = (0, token_utils_1.getATAAddressSync)({ mint, owner: this.ctx.wallet.publicKey });
            const toTokenAccountInfo = yield this.ctx.connection.getAccountInfo(to);
            const tx = new common_sdk_1.TransactionBuilder(this.ctx.provider.connection, this.ctx.provider.wallet);
            if (toTokenAccountInfo === null) {
                tx.addInstruction({
                    instructions: [(0, token_utils_1.createATAInstruction)({ address: to, mint, owner: this.ctx.wallet.publicKey, payer: this.ctx.wallet.publicKey })],
                    cleanupInstructions: [],
                    signers: [],
                });
            }
            const newClaimIx = yield this.ctx.methods.newClaim({
                accounts: {
                    distributor: distributor.key,
                    claimStatus: claimStatus.key,
                    from,
                    to,
                    claimant: this.ctx.wallet.publicKey,
                },
                inputs: {
                    index,
                    amount,
                    proof: proof.map((p) => (0, utils_1.toBytes32Array)(p)),
                },
            });
            if (newClaimIx.ix) {
                tx.addInstruction(newClaimIx.ix);
            }
            return tx;
        });
    }
    clawback(mint, amount, version = new anchor_1.BN(1)) {
        return __awaiter(this, void 0, void 0, function* () {
            const distributor = this.pda.distributor(mint, version);
            const distributorData = yield this.ctx.program.account.merkleDistributor.fetch(distributor.key);
            if (!distributorData) {
                throw new Error(`Distributor of token ${mint.toBase58()} version ${version.toString()} not found`);
            }
            if (distributorData.admin.toBase58() !== this.ctx.wallet.publicKey.toBase58()) {
                throw new Error(`Admin of distributor ${distributor.key.toBase58()} is not the current wallet`);
            }
            amount = amount.gt(new anchor_1.BN(0)) ? amount : new anchor_1.BN(0);
            const from = (0, token_utils_1.getATAAddressSync)({ mint, owner: distributor.key });
            const tx = (yield this.ctx.methods.clawback({
                accounts: {
                    distributor: distributor.key,
                    from,
                    to: distributorData.clawbackReceiver,
                    claimant: this.ctx.wallet.publicKey,
                },
                inputs: { amount },
            })).toTx();
            return tx;
        });
    }
}
exports.DistributorClient = DistributorClient;
